import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Copy, ShieldCheck, ArrowRight, AlertTriangle, Clock, Share2, Flame, PauseCircle } from 'lucide-react';
import { accountsAPI, fmtError, certificatesAPI, plansAPI } from '@/lib/api';
import { formatInt, prettyPhase, prettyPlan, nextRequestForAccount } from '@/lib/format';
import ShareCertModal from '@/components/ShareCertModal';

const CRYPTOS = [
  { code: 'usdtbsc', name: 'Tether USDT', networks: ['BEP-20'] },
  { code: 'usdcbsc', name: 'USDC', networks: ['BEP-20'] },
  { code: 'usdtmatic', name: 'Tether USDT', networks: ['Polygon'] },
  { code: 'btc', name: 'Bitcoin (BTC)', networks: ['BTC'] },
  { code: 'eth', name: 'Ethereum (ETH)', networks: ['ERC-20'] },
  { code: 'ltc', name: 'Litecoin (LTC)', networks: ['LTC'] },
  { code: 'sol', name: 'Solana (SOL)', networks: ['SOL'] },
  { code: 'trx', name: 'TRON (TRX)', networks: ['TRX'] },
];

export default function AccountDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [err, setErr] = useState('');
  const [reqOpen, setReqOpen] = useState(false);
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('');
  const [splitPlan, setSplitPlan] = useState(null);
  const [unlockCurrency, setUnlockCurrency] = useState('usdtbsc');

  // payout form
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', cryptocurrency: 'usdtbsc', network: 'BEP-20', wallet: '' });

  // share cert
  const [shareCert, setShareCert] = useState(null);
  const [relatedCerts, setRelatedCerts] = useState([]);

  const load = async () => {
    try {
      const acc = await accountsAPI.get(id);
      setA(acc);
      try {
        const list = await certificatesAPI.mine();
        setRelatedCerts(list.filter(c => c.account_id === id));
      } catch {}
      if (acc?.plan_id === 'split') {
        try { setSplitPlan(await plansAPI.get('split')); } catch {}
      }
    } catch (e) { setErr(fmtError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const requestPhase = async () => {
    setStatus('');
    try {
      const res = await accountsAPI.requestPhase(id, note, unlockCurrency);
      if (res?.requires_payment && res?.order_id) {
        setStatus('Redirecting to unlock checkout…');
        setTimeout(() => nav(`/checkout/${res.order_id}`), 400);
        return;
      }
      setStatus('Request submitted · awaiting admin review.');
      setReqOpen(false); setNote('');
      load();
    } catch (e) { setStatus(fmtError(e)); }
  };

  const submitPayout = async (e) => {
    e.preventDefault();
    setStatus('');
    try {
      const { payoutsAPI } = await import('@/lib/api');
      await payoutsAPI.request({
        account_id: id,
        amount: Number(payForm.amount),
        cryptocurrency: payForm.cryptocurrency,
        network: payForm.network,
        wallet: payForm.wallet,
      });
      setStatus('Payout request submitted · awaiting admin review.');
      setPayoutOpen(false);
      setPayForm({ amount: '', cryptocurrency: 'usdtbsc', network: 'BEP-20', wallet: '' });
      load();
    } catch (e) { setStatus(fmtError(e)); }
  };

  if (err) return <div className="text-[#ff3366]">{err}</div>;
  if (!a) return <div className="text-zinc-500 font-mono text-sm">LOADING…</div>;

  const next = nextRequestForAccount(a);
  const isBreached = a.status === 'breached';
  const isPaused = !!a.is_paused;
  const hasPending = !!a.pending_request;

  const copy = (txt) => navigator.clipboard.writeText(txt);

  return (
    <div className={`space-y-6 ${isBreached || isPaused ? 'opacity-80' : ''}`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-sm mb-2">Simulated account · #{a.mt_login}</div>
          <h1 className="font-display text-3xl">${formatInt(a.account_size)} · {prettyPlan(a.plan_id)}</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {isPaused ? (
              <span className="chip chip-yellow" data-testid="paused-badge">PAUSED</span>
            ) : (
              <span className={`chip ${isBreached ? 'chip-red' : a.phase === 'funded' ? 'chip-green' : 'chip-yellow'}`}>{prettyPhase(a.phase)}</span>
            )}
            {hasPending && !isBreached && !isPaused && (
              <span className="chip chip-yellow" data-testid="pending-badge">
                <Clock size={10} className="inline mr-1" /> PENDING {a.pending_request?.target_phase ? `· ${a.pending_request.target_phase.toUpperCase().replace('_',' ')}` : ''}
              </span>
            )}
            {isBreached && <span className="text-xs text-[#ff3366]">Reason: {a.breach_reason || '—'}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {isBreached && (
            <Link to="/plans" className="btn-primary btn-sm" data-testid="buy-new-after-breach">
              Purchase new program <ArrowRight size={14} />
            </Link>
          )}
          {!isBreached && !isPaused && hasPending && a.pending_request?.type === 'split_unlock_payment' && (
            <button
              className="btn-primary btn-sm"
              onClick={() => nav(`/checkout/${a.pending_request.order_id}`)}
              data-testid="resume-unlock-checkout"
            >
              Complete unlock payment <ArrowRight size={14} />
            </button>
          )}
          {!isBreached && !isPaused && !hasPending && next?.type === 'phase_upgrade' && (
            <button className="btn-primary btn-sm" onClick={() => setReqOpen(v => !v)} data-testid="request-phase-upgrade">
              {next.label}
            </button>
          )}
          {!isBreached && !isPaused && !hasPending && next?.type === 'split_unlock' && (
            <button className="btn-primary btn-sm" onClick={() => setReqOpen(v => !v)} data-testid="request-phase-upgrade">
              {next.label}
            </button>
          )}
          {!isBreached && !isPaused && !hasPending && next?.type === 'payout' && (
            <button className="btn-primary btn-sm" onClick={() => setPayoutOpen(v => !v)} data-testid="request-payout-btn">
              Request Payout
            </button>
          )}
        </div>
      </div>

      {isPaused && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-5 flex gap-3 items-start" data-testid="paused-banner">
          <PauseCircle size={18} className="text-amber-400 mt-0.5" />
          <div>
            <div className="font-display text-lg">Account paused</div>
            <div className="text-sm text-zinc-300 mt-1 leading-relaxed">
              {a.paused_reason || 'Awaiting completion of account migration by the new management team.'}
              {' '}Phase upgrades and payout requests are temporarily disabled on this account. Contact support if you have any questions.
            </div>
          </div>
        </div>
      )}

      {isBreached && (
        <div className="rounded-lg border border-[#ff3366]/30 bg-[#ff3366]/5 p-4" data-testid="breach-banner">
          <div className="flex gap-3 items-start">
            <AlertTriangle size={16} className="text-[#ff3366] mt-1 shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-lg">This account was breached</div>
              <div className="text-sm text-zinc-400 mt-1">{a.breach_reason || 'No reason provided.'} To continue, please purchase a new evaluation program.</div>
            </div>
          </div>
          {a.has_breach_proof && a.breach_proof_image && (
            <div className="mt-3 pl-7">
              <div className="label-xs mb-2">EVIDENCE SCREENSHOT</div>
              <a
                href={a.breach_proof_image}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block rounded-md overflow-hidden border border-white/10 bg-black/40 hover:border-white/25 transition"
                data-testid="breach-proof-link"
              >
                <img
                  src={a.breach_proof_image}
                  alt="Breach evidence screenshot"
                  className="block max-h-72 max-w-full object-contain bg-black"
                  data-testid="breach-proof-image"
                />
              </a>
              <div className="text-[11px] text-zinc-500 mt-2 font-mono tracking-wider uppercase">Click to open full size</div>
            </div>
          )}
        </div>
      )}

      {reqOpen && !isBreached && next?.type === 'split_unlock' && splitPlan && (() => {
        const sz = a.account_size;
        const original = splitPlan.remainder_table?.[sz];
        // Locked-in discount % from the trading account doc takes precedence —
        // a trader who bought during 70% off keeps 70% off even after the promo ends.
        const lockedPct = Number(a.locked_unlock_discount_pct || 0);
        const livePct = Number(splitPlan.remainder_discount_pct || 0);
        const discount = lockedPct > 0 ? lockedPct / 100.0 : livePct;
        const payable = original ? Math.ceil(original * (1 - discount) - 1e-9) : null;
        const hasPromo = discount > 0;
        const promoTag = lockedPct > 0
          ? `LOCKED IN · ${Math.round(lockedPct)}% OFF UNLOCK FEE`
          : (splitPlan.remainder_promo_tag || '');
        return (
          <div className="card-tech rounded-lg p-6 relative overflow-hidden" data-testid="split-unlock-panel">
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(0,255,102,0.18), transparent)' }} />
            <div className="relative">
              {hasPromo && promoTag && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#ff6b1a]/40 bg-[#ff6b1a]/10" data-testid="unlock-promo-tag">
                  <Flame size={12} className="text-[#ff8c42]" />
                  <span className="text-xs font-mono tracking-wider text-[#ff8c42]">{promoTag}</span>
                </div>
              )}
              <div className={`label-sm ${hasPromo ? 'mt-4' : ''}`}>Pay unlock fee to activate funded phase</div>
              <div className="flex items-end gap-4 mt-2">
                <div>
                  <div className="font-display text-4xl" data-testid="unlock-payable">${formatInt(payable)}</div>
                  <div className="text-xs text-zinc-500 mt-1">You pay now</div>
                </div>
                {hasPromo && original && payable < original && (
                  <div className="pb-2">
                    <div className="text-lg text-zinc-500 line-through font-mono" data-testid="unlock-original">${formatInt(original)}</div>
                    <div className="text-xs text-[#00ff66] mt-1">Save ${formatInt(original - payable)}</div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                <div>
                  <div className="label-xs mb-1">Pay with</div>
                  <select className="input-tech" value={unlockCurrency} onChange={e => setUnlockCurrency(e.target.value)} data-testid="unlock-currency">
                    <option value="usdtbsc">USDT · BEP-20 (BSC)</option>
                    <option value="usdcbsc">USDC · BEP-20 (BSC)</option>
                    <option value="usdtmatic">USDT · Polygon</option>
                    <option value="btc">Bitcoin (BTC)</option>
                    <option value="eth">Ethereum (ETH)</option>
                    <option value="ltc">Litecoin (LTC)</option>
                    <option value="sol">Solana (SOL)</option>
                    <option value="trx">TRON (TRX)</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <div className="label-xs mb-1">Note to admin (optional)</div>
                  <textarea className="input-tech" rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. passed 15% objective, ready for funded" data-testid="phase-note" />
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button className="btn-primary btn-sm" onClick={requestPhase} data-testid="submit-phase-req">
                  Continue to checkout <ArrowRight size={14} />
                </button>
                <button className="btn-ghost btn-sm" onClick={() => setReqOpen(false)}>Cancel</button>
              </div>
              <div className="mt-3 text-xs text-zinc-500">After payment confirms, your funded phase will enter the admin approval queue (typically &lt; 24h).</div>
              {status && <div className="mt-3 text-sm text-zinc-400">{status}</div>}
            </div>
          </div>
        );
      })()}

      {reqOpen && !isBreached && next?.type === 'phase_upgrade' && (
        <div className="card-tech rounded-lg p-5" data-testid="phase-request-panel">
          <div className="label-sm mb-2">{next?.label} — note to admin (optional)</div>
          <textarea className="input-tech" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder={`e.g. objective reached, requesting ${next?.target?.replace('_',' ')}`} data-testid="phase-note" />
          <div className="mt-3 flex gap-2">
            <button className="btn-primary btn-sm" onClick={requestPhase} data-testid="submit-phase-req">Submit request</button>
            <button className="btn-ghost btn-sm" onClick={() => setReqOpen(false)}>Cancel</button>
          </div>
          {status && <div className="mt-3 text-sm text-zinc-400">{status}</div>}
        </div>
      )}

      {payoutOpen && !isBreached && (
        <form onSubmit={submitPayout} className="card-tech rounded-lg p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="payout-request-panel">
          <div className="sm:col-span-2 label-sm">Request a performance reward</div>
          <div>
            <div className="label-xs mb-1">Amount (USD)</div>
            <input required type="number" step="0.01" min="50" className="input-tech" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payout-amount" />
          </div>
          <div>
            <div className="label-xs mb-1">Cryptocurrency</div>
            <select className="input-tech" value={payForm.cryptocurrency} onChange={e => setPayForm({ ...payForm, cryptocurrency: e.target.value, network: CRYPTOS.find(c => c.code === e.target.value)?.networks[0] || 'BEP-20' })} data-testid="payout-crypto">
              {CRYPTOS.map(c => <option key={c.code} value={c.code}>{c.name} · {c.networks[0]}</option>)}
            </select>
          </div>
          <div>
            <div className="label-xs mb-1">Network</div>
            <select className="input-tech" value={payForm.network} onChange={e => setPayForm({ ...payForm, network: e.target.value })} data-testid="payout-network">
              {(CRYPTOS.find(c => c.code === payForm.cryptocurrency)?.networks || ['BEP-20']).map(n => <option key={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <div className="label-xs mb-1">Wallet address</div>
            <input required className="input-tech font-mono" value={payForm.wallet} onChange={e => setPayForm({ ...payForm, wallet: e.target.value })} data-testid="payout-wallet" />
          </div>
          <div className="sm:col-span-2 flex gap-2">
            <button className="btn-primary btn-sm" data-testid="submit-payout">Submit payout request</button>
            <button type="button" className="btn-ghost btn-sm" onClick={() => setPayoutOpen(false)}>Cancel</button>
          </div>
          {status && <div className="sm:col-span-2 text-sm text-[#00ff66]">{status}</div>}
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-tech rounded-lg p-6 relative">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="label-sm">Simulated Account Size</div>
              <div className="font-display text-3xl mt-1">${formatInt(a.account_size)}</div>
              <div className="text-xs font-mono text-zinc-500 mt-1">{prettyPlan(a.plan_id)} · {prettyPhase(a.phase)}</div>
            </div>
            {(() => {
              const pnl = Number(a.manual_pnl_usd ?? 0);
              const hasManual = a.manual_pnl_usd !== null && a.manual_pnl_usd !== undefined;
              const pos = pnl >= 0;
              const pct = a.account_size ? (pnl / Number(a.account_size)) * 100 : 0;
              if (!hasManual) {
                return (
                  <div className="text-right">
                    <div className="label-xs">CURRENT PNL</div>
                    <div className="font-mono text-zinc-500 text-xs mt-1">Awaiting admin update</div>
                  </div>
                );
              }
              return (
                <div className="text-right" data-testid="acc-pnl-block">
                  <div className="label-xs">CURRENT PNL</div>
                  <div className={`font-display text-3xl mt-1 ${pos ? 'text-[#00ff66]' : 'text-[#ff3366]'}`} data-testid="acc-pnl-value">
                    {pos ? '+' : '-'}${formatInt(Math.abs(pnl).toFixed(2))}
                  </div>
                  <div className={`text-xs font-mono mt-1 ${pos ? 'text-[#00ff66]/70' : 'text-[#ff3366]/70'}`}>
                    {pos ? '+' : ''}{pct.toFixed(2)}%
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Equity Curve — Coming Soon */}
          <div className="mt-4 h-60 relative border border-white/5 rounded-lg overflow-hidden bg-gradient-to-b from-[#0a0a0a] to-black flex items-center justify-center" data-testid="equity-coming-soon">
            <div className="text-center">
              <div className="chip chip-green mb-3"><span className="pulse-dot" /> COMING SOON</div>
              <div className="font-display text-xl text-white">Live Equity Curve</div>
              <div className="text-xs text-zinc-500 mt-1 max-w-xs">Real-time performance analytics will appear here once our MT5 bridge is live.</div>
            </div>
            <svg viewBox="0 0 600 120" className="absolute inset-0 w-full h-full opacity-10" preserveAspectRatio="none">
              <polyline fill="none" stroke="#00ff66" strokeWidth="1"
                points="0,80 40,70 80,75 120,60 160,55 200,50 240,40 280,45 320,35 360,30 400,32 440,22 480,18 520,25 560,15 600,22"/>
            </svg>
          </div>
        </div>

        <div className="card-tech rounded-lg p-6">
          <div className="label-sm">MT5 credentials</div>
          {a.mt_login ? (
            <>
              <Row k="Broker" v={a.mt_broker || 'Deriv'} copy={() => copy(a.mt_broker || 'Deriv')} />
              <Row k="Server" v={a.mt_server} copy={() => copy(a.mt_server)} />
              <Row k="Login" v={a.mt_login} copy={() => copy(a.mt_login)} />
              <Row k="Password" v={a.mt_password} copy={() => copy(a.mt_password)} mono />
              {a.mt_investor_password && <Row k="Investor password" v={a.mt_investor_password} copy={() => copy(a.mt_investor_password)} mono />}
              <div className="mt-4 text-xs text-zinc-500 flex items-start gap-2">
                <ShieldCheck size={12} className="text-[#00ff66] mt-0.5" />
                Use these credentials with MetaTrader 5 desktop or mobile.
              </div>
            </>
          ) : (
            <div className="text-sm text-zinc-500 mt-3">Credentials will be assigned by admin.</div>
          )}
        </div>
      </div>

      {!isBreached && <PerformancePanel a={a} />}

      {/* Certificates related to this account */}
      {relatedCerts.length > 0 && (
        <div className="card-tech rounded-lg p-6">
          <div className="label-sm">Certificates earned on this account</div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {relatedCerts.map(c => (
              <div key={c.certificate_id} className="border border-white/5 rounded-md p-4 flex items-center justify-between" data-testid={`related-cert-${c.certificate_id}`}>
                <div>
                  <div className="label-xs">{(c.cert_type || '').replace(/_/g, ' ').toUpperCase()}</div>
                  <div className="text-xs text-zinc-500 mt-1">{new Date(c.issued_at).toLocaleDateString()}</div>
                </div>
                <button className="btn-ghost btn-sm" onClick={() => setShareCert(c)}><Share2 size={12} className="inline mr-1" /> Share</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-tech rounded-lg p-6">
          <div className="label-sm">Program</div>
          <Row k="Program" v={prettyPlan(a.plan_id)} />
          <Row k="Size" v={`$${formatInt(a.account_size)}`} />
          <Row k="Phase" v={prettyPhase(a.phase)} />
          <Row k="Status" v={isBreached ? 'Breached' : (hasPending ? 'Pending review' : 'Active')} />
          <Row k="Created" v={new Date(a.created_at).toLocaleDateString()} />
          {a.funded_at && <Row k="Funded since" v={new Date(a.funded_at).toLocaleDateString()} />}
        </div>
        <div className="card-tech rounded-lg p-6">
          <div className="label-sm">Program rules (applied)</div>
          <div className="text-sm text-zinc-500 mt-3">Please refer to your program's Rules page for full details on drawdowns, objectives and consistency.</div>
          <Link to="/rules" className="btn-ghost btn-sm mt-4 inline-flex">Open Rules page →</Link>
        </div>
      </div>

      {shareCert && <ShareCertModal cert={shareCert} onClose={() => setShareCert(null)} />}
    </div>
  );
}

function Row({ k, v, copy, mono }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <span className="text-sm text-zinc-400">{k}</span>
      <div className="flex items-center gap-2">
        <span className={`text-sm ${mono ? 'font-mono' : ''}`}>{v}</span>
        {copy && <button onClick={copy} className="text-zinc-500 hover:text-white" aria-label="copy"><Copy size={12} /></button>}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Progress + breach meters — real data from account.progress / .metrics / .dd_limits
// --------------------------------------------------------------------------

function PerformancePanel({ a }) {
  const hasManual = a.manual_pnl_usd !== null && a.manual_pnl_usd !== undefined;
  if (!hasManual) {
    return (
      <div className="card-tech rounded-lg p-6 text-center" data-testid="performance-panel-empty">
        <div className="label-sm">Progress &amp; risk meters</div>
        <div className="text-sm text-zinc-500 mt-3">These will populate once admin records your first PnL update.</div>
      </div>
    );
  }

  const size = Number(a.account_size || 0);
  const dailyLimitPct = Number(a.dd_limits?.daily_dd || 0);
  const maxLimitPct = Number(a.dd_limits?.max_dd || 0);
  const dailyUsedPct = Number(a.metrics?.daily_dd_pct || 0);
  const maxUsedPct = Number(a.metrics?.max_dd_pct || 0);
  // The daily % is relative to TODAY'S starting equity, not the fixed account
  // size — use the real baseline the backend tracked so the dollar figures
  // below are exact, not approximated off account_size.
  const dailyBase = Number(a.metrics?.daily_start_equity_usd || size);

  const dailyBudgetUsedPct = dailyLimitPct ? (dailyUsedPct / dailyLimitPct) * 100 : 0;
  const maxBudgetUsedPct = maxLimitPct ? (maxUsedPct / maxLimitPct) * 100 : 0;

  return (
    <div className="space-y-6">
      <ProgressToTarget a={a} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <DrawdownGauge
          label="Daily drawdown"
          sublabel={`Resets daily · limit ${dailyLimitPct.toFixed(1)}%`}
          usedPct={dailyBudgetUsedPct}
          usedDollars={dailyBase * (dailyUsedPct / 100)}
          limitDollars={dailyBase * (dailyLimitPct / 100)}
        />
        <DrawdownGauge
          label="Max drawdown"
          sublabel={`From initial size · limit ${maxLimitPct.toFixed(1)}%`}
          usedPct={maxBudgetUsedPct}
          usedDollars={size * (maxUsedPct / 100)}
          limitDollars={size * (maxLimitPct / 100)}
        />
      </div>
    </div>
  );
}

function ProgressToTarget({ a }) {
  const p = a.progress;
  if (!p?.target_pct) return null; // funded accounts have no further target to climb toward

  const pct = Math.min(100, Math.max(0, p.progress_pct));
  const size = Number(a.account_size || 0);
  const targetDollars = size * (p.target_pct / 100);
  const gainedDollars = Number(a.manual_pnl_usd ?? 0);
  const remainingDollars = Math.max(0, targetDollars - gainedDollars);

  return (
    <div className="card-tech rounded-lg p-6" data-testid="progress-to-target-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-sm">Profit target progress</div>
          <div className="text-xs text-zinc-500 mt-1">{prettyPhase(a.phase)} target — {p.target_pct.toFixed(1)}% of account size</div>
        </div>
        <div className="font-mono text-2xl text-[#00ff66]">
          {Math.round(pct)}%<span className="text-xs text-zinc-500 ml-1">of target</span>
        </div>
      </div>
      <div className="h-3.5 rounded-full bg-white/5 overflow-hidden mt-4">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#00ff66]/60 to-[#00ff66] transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/5 text-sm">
        <div>
          <div className="label-xs">Gained</div>
          <div className="font-mono mt-1 text-[#00ff66]">+${gainedDollars.toFixed(2)}</div>
        </div>
        <div>
          <div className="label-xs">Target</div>
          <div className="font-mono mt-1">${targetDollars.toFixed(2)}</div>
        </div>
        <div>
          <div className="label-xs">Remaining</div>
          <div className="font-mono mt-1">${remainingDollars.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
}

function DrawdownGauge({ label, sublabel, usedPct, usedDollars, limitDollars }) {
  const clamped = Math.min(100, Math.max(0, usedPct));
  const status = clamped >= 90 ? 'critical' : clamped >= 70 ? 'caution' : 'safe';
  const color = status === 'critical' ? '#ff3366' : status === 'caution' ? '#ffb020' : '#00ff66';
  const remaining = Math.max(0, limitDollars - usedDollars);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const testId = `gauge-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className="card-tech rounded-lg p-5" data-testid={testId}>
      <div className="label-sm">{label}</div>
      <div className="text-xs text-zinc-500 mt-0.5">{sublabel}</div>

      <div className="relative w-full max-w-[220px] mx-auto mt-2">
        <svg viewBox="0 0 200 130" className="w-full h-auto overflow-visible">
          <path d="M 26 106 A 74 74 0 0 1 174 106" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="13" strokeLinecap="round" />
          <path
            d="M 26 106 A 74 74 0 0 1 174 106"
            fill="none" stroke={color} strokeWidth="13" strokeLinecap="round"
            pathLength="100"
            style={{
              strokeDasharray: mounted ? `${clamped} 100` : '0 100',
              transition: 'stroke-dasharray 900ms cubic-bezier(.22,.9,.26,1)',
            }}
          />
          <line x1="136.4" y1="55.9" x2="143.5" y2="46.1" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
          <line x1="159.0" y1="86.9" x2="170.4" y2="83.1" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
        </svg>
        <div className="absolute inset-x-0 top-[58%] flex flex-col items-center gap-1.5">
          <span className="font-mono text-2xl" style={{ color }}>{Math.round(clamped)}%</span>
          <span
            className="text-[10px] font-mono tracking-wider uppercase px-2 py-0.5 rounded-full"
            style={{ color, background: `${color}1f`, border: `1px solid ${color}40` }}
          >
            {status}
          </span>
        </div>
      </div>

      <div className="flex justify-between mt-3 pt-3 border-t border-white/5 text-xs">
        <div>
          <div className="label-xs">Limit</div>
          <div className="font-mono mt-1">${limitDollars.toFixed(2)}</div>
        </div>
        <div className="text-right">
          <div className="label-xs">Cushion left</div>
          <div className="font-mono mt-1 text-[#00ff66]">${remaining.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
}